import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const GhanaPage = () => {
  const ghanaData = {
    name: 'Ghana',
    region: 'West Africa',
    color: '#708080',
    metaTitle: 'Pharma & Nutraceutical Registration in Ghana | FDA Ghana | WA-MRH | TheApostrophe',
    metaDescription: 'Register pharmaceutical and nutraceutical products in Ghana via FDA Ghana — a WHO Maturity Level 3 NRA. CTD dossier preparation, fast track pathway, WA-MRH multi-country submission, and NHIS-aligned portfolio strategy.',

    hero: {
      title: 'Pharma & Nutraceutical Registration in Ghana',
      subtitle: 'A WA-MRH–anchoring West African market with one of the region\'s most structured regulatory authorities, strong NHIS-backed demand, and a dual infectious-NCD disease burden creating consistent opportunities for Indian generic manufacturers.',
      cta: 'Start Your Ghana Registration',
      secondaryCta: null
    },

    atAGlance: [
      { label: 'Population', value: '~34.4 million' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 60–80' },
      { label: 'Health Expenditure (% of GDP)', value: '~4.8–5.2%' },
      { label: 'Urbanisation', value: '~58–60% urban' },
      { label: 'Life Expectancy', value: '~69 years' }
    ],

    marketOverview: {
      title: 'Market Overview: Structured, Insurance-Backed, and Import-Driven',
      stats: [
        'Ghana imports approximately 70% of its finished pharmaceuticals — primarily from India, China, and a small number of European suppliers',
        'India is the dominant source of generics entering the Ghanaian market; Indian-manufactured products are familiar, accepted, and price-competitive',
        'The National Health Insurance Scheme (NHIS) is a critical market driver — it covers essential medicines for a broad population, creating sustained, predictable demand for generics on the NHIS reimbursement list',
        'Ghana\'s FDA (Food and Drugs Authority) is one of West Africa\'s most capable and internationally recognized regulatory bodies — a WHO Maturity Level 3 NRA and a Regional Centre of Regulatory Excellence (RCORE) for Medicine Evaluation and Registration in Africa',
        'Ghana FDA achieved WHO-Prequalified Quality Control Laboratory status in 2022 — the first laboratory in West or Central Africa to do so',
        'Private sector retail (pharmacies, licensed chemical sellers) plays a major role — stock-outs in public facilities push significant volumes through private channels',
        'Ghana participates in the WA-MRH (West Africa Medicines Regulatory Harmonization) initiative under ECOWAS — a single joint assessment process covering multiple ECOWAS member states'
      ],
      opportunities: [
        'NHIS reimbursement drives reliable institutional demand for essential generics',
        '70% import dependency leaves abundant space for quality Indian-manufactured generics',
        'Dual disease burden (infectious + NCD) creates demand across a wide product portfolio',
        'Ghana\'s structured FDA means compliant dossiers clear faster than in less-developed NRAs',
        'WA-MRH pathway offers multi-country registration efficiency for qualifying products',
        'Growing urban middle class is increasingly receptive to nutraceuticals and wellness products',
        'Public sector tender system (Ghana Health Service) offers volume opportunity for anti-infectives, antimalarials, and maternal health products'
      ]
    },

    diseaseburden: {
      title: 'Disease Landscape: Ghana\'s Healthcare Needs',
      subtitle: 'Ghana carries a significant dual disease burden — persistent high-impact infectious diseases alongside rapidly growing non-communicable conditions driven by urbanisation and changing lifestyles.',
      categories: [
        {
          name: 'Infectious Diseases – Major Demand Drivers',
          items: [
            'Malaria: Ghana is among the 15 highest-burden malaria countries globally — approximately 2.5% of global malaria cases and 1.9% of global malaria deaths; ~5.4 million cases reported in 2021; deaths skewed heavily toward children under 5',
            'Tuberculosis: Incidence of ~136 per 100,000 population; TB-HIV co-infection remains a compounding challenge',
            'HIV/AIDS: Ghana has not yet met the 95-95-95 targets; HIV prevalence is approximately 1.7–2% nationally; ongoing ARV and co-infection management needs',
            'Respiratory infections and diarrhoeal diseases: Major contributors to under-5 morbidity',
            'Portfolio: Antimalarials (ACT — artemether-lumefantrine, artesunate-amodiaquine), antibiotics (amoxicillin, cotrimoxazole, azithromycin), ORS + zinc, paediatric suspensions, antifungals, antiparasitics'
          ]
        },
        {
          name: 'Growing Non-Communicable Diseases',
          items: [
            'Hypertension: Rapidly increasing, particularly in urban Accra, Kumasi, and Takoradi — lifestyle and dietary shifts are primary drivers',
            'Diabetes: Prevalence growing; low diagnosis rates mean a large unaddressed patient pool',
            'Cardiovascular disease: Rising in working-age adults and the urban elderly',
            'Mental health: Emerging awareness and treatment gap',
            'Portfolio: Amlodipine, lisinopril, atenolol, metformin, glibenclamide, atorvastatin, aspirin',
            'Affordable, WHO-GMP-certified generics are the preferred and reimbursable treatment option'
          ]
        },
        {
          name: 'Maternal, Paediatric & Nutritional Health',
          items: [
            'Ongoing national programmes for maternal anaemia, malaria in pregnancy (IPTp), and child nutrition',
            'Iron + folic acid, calcium + D3, prenatal multivitamins in consistent demand',
            'Paediatric multivitamin syrups, dewormers (albendazole, mebendazole), zinc + ORS',
            'Strong donor and government procurement for RMNCH (Reproductive, Maternal, Newborn, and Child Health) product categories',
            'Nutraceuticals: Urban Ghanaian consumers are increasingly health-conscious — blood tonics, immunity supplements, vitamins, herbal wellness products, and weight management products are gaining pharmacy shelf presence in Accra and Kumasi'
          ]
        }
      ]
    },

    regulatoryFramework: {
      authority: 'FDA Ghana — Food and Drugs Authority',
      description: 'Ghana\'s Food and Drugs Authority (FDA) operates under the Public Health Act, 2012 (Act 851). It is one of the most structured and internationally benchmarked regulatory agencies in West Africa, having achieved WHO Global Benchmarking Tool (GBT) Maturity Level 3 in 2020, with its quality control laboratory WHO-Prequalified in 2022 — a reference-standard institution for the West Africa region. The FDA is also designated as a Regional Centre of Regulatory Excellence (RCORE) for Medicine Evaluation and Registration and serves as an anchor NRA in the WA-MRH joint assessment process.',
      pharma: {
        timeline: 'Standard: 12–18 months for new applications | Fast Track: ~3 months (90 days) for qualifying products | Renewal: ~3 months (90 days) | High/Low Risk: 6 months (180 days) | Registration validity: 5 years (renewal must be initiated at least 3 months before expiry)',
        requirements: [
          'All applications by foreign manufacturers must be submitted through a locally appointed agent — a registered pharmaceutical wholesale company or accredited manufacturer\'s representative in Ghana',
          'CTD format (ICH M4-aligned) accepted and preferred by FDA Ghana',
          'Module 1 (Administrative) in English; all labelling in English',
          'Stability data required for tropical climatic conditions (ICH Zone IVb: 40°C/75% RH)',
          'WHO-GMP certification widely accepted; FDA Ghana may conduct or request GMP site inspections for some product categories',
          'Product samples must be submitted as per the FDA sample schedule',
          'COA for at least three (3) batches of the finished pharmaceutical product (FPP) required',
          'Signed specifications for APIs and FPP required',
          'Fast Track pathway available for products addressing unmet medical needs or qualifying under specific FDA guidelines',
          'Applications must be accompanied by fees at time of submission — no fee, no acceptance',
          'No eCTD submission portal currently operational; electronic copies plus physical submissions required; status tracking requires applicant to visit FDA offices in person',
          'Application categories include New Registration, Renewal of Registration, and Variation of a Registered Product (per FDA Ghana\'s Variation Guidelines)',
          'Critical renewal note: Failure to renew within 3 years of expiry results in the registration being treated as a brand-new application — with full fees and timeline',
          'WA-MRH joint assessment pathway available via Ghana FDA (co-anchor NRA with NAFDAC Nigeria) — single CTD dossier jointly assessed across participating ECOWAS NRAs (Ghana, Nigeria, Burkina Faso, Côte d\'Ivoire, Senegal, Togo, Sierra Leone); 4 joint assessment meetings per year; query responses within 60 days (round 1) and 30 days (round 2)'
        ],
        insight: 'Ghana\'s FDA is structured and process-oriented. Well-prepared, complete dossiers navigate the system efficiently. Deficiency letters are issued for gaps in CTD quality sections, stability data, labelling, or API documentation. The fast track pathway is a genuine advantage for qualifying generics — it can cut 9–12 months off the standard timeline. For Indian manufacturers targeting multiple ECOWAS markets simultaneously, WA-MRH through Ghana FDA is the most efficient multi-country pathway available in West Africa — Ghana\'s WHO Maturity Level 3 status gives its anchor role real credibility with other participating NRAs.'
      },
      nutra: {
        timeline: 'Varies based on classification, claims, and dossier completeness',
        requirements: [
          'Ghana FDA has a dedicated Food Supplement Registration unit and a structured process for market authorization of food/nutritional/dietary supplements and nutraceuticals — both for domestic sale and for export',
          'Full quantitative formula',
          'Manufacturer GMP/QMS certification',
          'Certificate of Analysis (COA) with microbial and chemical specifications',
          'Safety data for any non-traditional, novel, or high-risk ingredients',
          'CFS (Certificate of Free Sale) from country of origin',
          'Label and artwork in English — claims must be reviewed for regulatory compliance',
          'Claims must not make medicinal, therapeutic, or disease-treatment assertions',
          'Products with herbal origin may be assessed under the Herbal and Homeopathic Medicines Department — a separate dedicated pathway'
        ],
        insight: 'Ghana\'s nutraceutical and supplement market is growing rapidly in urban areas. The NHIS does not cover supplements, so retail and private pharmacy channels dominate. Blood tonics, immune boosters, multivitamins, herbal products, and weight management supplements all have active consumer demand in Accra, Kumasi, and Cape Coast.'
      }
    },

    pharmaPathway: {
      title: 'Pharmaceutical Registration (FDA Ghana — Standard & WA-MRH)',
      steps: [
        'Local Agent Appointment — Appoint a registered pharmaceutical wholesale company or accredited manufacturer\'s representative in Ghana',
        'Application Category Determination — New registration, renewal, or variation',
        'Fast Track Eligibility Check — Assess whether the product qualifies for 90-day fast track processing',
        'Pathway Selection — National (FDA Ghana only) vs WA-MRH joint assessment for multi-ECOWAS registration',
        'CTD Dossier Preparation — ICH CTD format; Module 1 in English; stability data (Zone IVb); GMP certificate; three-batch COA; API specifications',
        'Fee Calculation and Payment — Fees must accompany the application at submission',
        'Submission to FDA Ghana — Through local agent; electronic copies + physical submission to FDA headquarters, Accra',
        'FDA Screening — Completeness check; incomplete applications returned',
        'Technical Evaluation — CTD review across quality, safety, and efficacy modules (or joint assessment under WA-MRH)',
        'GMP Assessment — Review of manufacturing site GMP certificate; site inspection possible for some categories',
        'Product Sample Testing — FDA quality control laboratory may test submitted samples',
        'Query / Deficiency Response — Respond to FDA queries within stipulated timelines (60 days WA-MRH round 1; 30 days round 2)',
        'Marketing Authorization Issuance — Valid 5 years; registration certificate issued',
        'Renewal Initiation — Must begin at least 3 months before expiry'
      ]
    },

    nutraPathway: {
      title: 'Nutraceutical / Dietary Supplement Registration',
      steps: [
        'Product classification assessment — food supplement, nutraceutical, or herbal product',
        'Full quantitative formula preparation',
        'Manufacturer GMP/QMS certification',
        'COA + microbial and chemical specifications',
        'CFS / Certificate of Free Sale from India',
        'Safety data for novel or non-traditional ingredients',
        'English-language label and artwork preparation',
        'Claims language review — functional/nutritional claims only',
        'Submission to FDA Ghana Food Supplement Registration unit',
        'Evaluation and marketing authorization issuance'
      ]
    },

    requiredDocs: {
      'Pharmaceuticals': [
        'Completed FDA Ghana application form + cover letter',
        'CTD Modules 1–5 (Module 1 in English — administrative and prescribing information)',
        'WHO-GMP certificate of the manufacturing site',
        'CoPP (Certificate of Pharmaceutical Product) — strongly recommended',
        'Stability data (ICH Zone IVb: 40°C/75% RH)',
        'API/DMF documentation and dated, signed API specifications',
        'Finished product release specifications (dated and signed)',
        'Certificates of analysis for at least three (3) batches of FPP',
        'Product samples as per FDA sample schedule',
        'Labels and package inserts (English)',
        'Declaration by FPP manufacturer of API source',
        'Application and inspection fees (must accompany submission)'
      ],
      'Nutraceuticals': [
        'Full quantitative composition and COA',
        'GMP/QMS certificate',
        'Safety justification for non-traditional ingredients',
        'Artwork and label in English',
        'Product specifications',
        'CFS / Certificate of Free Sale from India'
      ]
    },

    whyThisCountry: {
      title: 'Why Ghana Should Be Part of Your West Africa Strategy',
      reasons: [
        'Among the most structured regulatory environments in West Africa — predictable, process-driven',
        'WHO Maturity Level 3 NRA with a WHO-Prequalified quality control laboratory',
        '70% pharmaceutical import dependency — market built for foreign generic manufacturers',
        'NHIS reimbursement creates reliable, recurring institutional demand for essential generics',
        'India is already the dominant supplier of generics — established import channels exist',
        'WA-MRH anchor role makes Ghana the strategic hub for multi-country ECOWAS market entry',
        'Dual disease burden (malaria + NCD) creates a wide product opportunity window',
        'Growing urban middle class (58–60% urbanisation) driving nutraceutical and OTC supplement uptake',
        'English-language market — no translation costs for labelling and documentation'
      ],
      bestPortfolio: [
        'Anti-infectives, antimalarials (ACT), paediatric formulations (suspensions, dispersible tablets)',
        'Chronic disease generics: antihypertensives (amlodipine, enalapril, atenolol), antidiabetics (metformin, glibenclamide), statins',
        'NHIS-listed essential generics for institutional/tender supply',
        'Maternal and paediatric nutraceuticals: iron-folic acid, multivitamins, calcium + D3, ORS, dewormers',
        'Blood tonics, immunity supplements, herbal wellness products for pharmacy retail'
      ]
    },

    howWeHelp: {
      title: 'How TheApostrophe Helps You Register in Ghana',
      services: [
        'Product-market feasibility analysis including NHIS list alignment and tender opportunity mapping',
        'Fast track eligibility assessment for qualifying products',
        'FDA Ghana CTD dossier preparation (standard and fast track pathways)',
        'WA-MRH multi-country dossier strategy and submission coordination',
        'English artwork and label compliance review',
        'Nutraceutical and food supplement documentation support',
        'Local agent / authorized representative identification in Ghana',
        'Product sample coordination and COA review',
        'Query handling, deficiency response management, and lifecycle support (renewals, variations)'
      ]
    },

    faqs: [
      {
        question: 'Does Ghana require a local agent for registration?',
        answer: 'Yes. Foreign manufacturers must appoint a local agent — a registered pharmaceutical wholesale company or accredited manufacturer\'s representative in Ghana. The application must be submitted through this agent.'
      },
      {
        question: 'What format does FDA Ghana accept for dossier submissions?',
        answer: 'FDA Ghana accepts dossiers in the ICH CTD (Common Technical Document) format as per ICH M4 guidelines. Module 1 must be in English. An eCTD electronic submission portal is not yet fully operational; electronic copies and physical submissions are currently required.'
      },
      {
        question: 'What is the registration validity period and when must renewal be initiated?',
        answer: 'Registration is valid for 5 years. Renewal must be applied for at least 3 months before expiry. If a product is not renewed within 3 years of expiry, the application is treated as a completely new registration — with full fees and timelines.'
      },
      {
        question: 'Is there a fast track pathway?',
        answer: 'Yes. FDA Ghana has a formal fast track pathway for qualifying products, with a target processing time of approximately 3 months (90 days). This is a significant advantage for products addressing priority health needs.'
      },
      {
        question: 'Is Indian GMP accepted by FDA Ghana?',
        answer: 'Yes. WHO-GMP certification from Indian manufacturing sites is accepted. FDA Ghana may conduct or request GMP site inspections for some product categories, particularly where manufacturing standards need verification.'
      },
      {
        question: 'What is WA-MRH and does it cover Ghana?',
        answer: 'WA-MRH (West Africa Medicines Regulatory Harmonization) is the ECOWAS regional joint medicine assessment process. Ghana FDA is one of its two anchor NRAs. Submitting through WA-MRH allows a single dossier to be assessed simultaneously for registration across multiple ECOWAS member states, dramatically reducing multi-market registration timelines and costs.'
      },
      {
        question: 'Do nutraceuticals require FDA Ghana registration?',
        answer: 'Yes. Food supplements and nutraceuticals intended for sale in Ghana require market authorization from FDA Ghana. A dedicated Food Supplement Registration unit handles these applications. Claims must be restricted to functional and nutritional language — no therapeutic or disease-treatment claims are permitted.'
      }
    ]
  };

  return <CountryTemplate countryData={ghanaData} />;
};
